/**
 * Central metric registry — drives every configurable widget on the
 * Overview (KPI tiles, chart lines, By-application columns, etc.).
 *
 * Each metric knows how to pick its value from the canonical response
 * shapes (OverviewKpis / DailyPoint / CampaignStatsRow / ByAppRow) and
 * how to format it for display. `isRate` flags ratio metrics so
 * aggregators know to recompute from the underlying sums rather than
 * summing the already-derived ratio.
 */

import type { ByAppRow, ByCountryRow, CampaignStatsRow, DailyPoint, OverviewKpis } from "./types";

/** Both ByApp and ByCountry rollups expose the same numeric metric
 *  fields — only the dimension column differs (app_id vs country). The
 *  metric registry reads the shared fields, so widgets can hand either
 *  shape to `fromByApp`. */
export type ByDimensionRow = Omit<ByAppRow, "app_id"> | Omit<ByCountryRow, "country">;

export type MetricUnit = "$" | "×" | "%" | "";
export type GoodDir = "up" | "down";

export type MetricDef = {
  id: string;
  label: string;
  short: string;
  unit: MetricUnit;
  color: string;
  goodDir: GoodDir;
  gated: boolean;
  isRate: boolean;
  fromKpis: (k: OverviewKpis) => number | null;
  fromSeries: (p: DailyPoint) => number | null;
  fromRow: (r: CampaignStatsRow) => number | null;
  fromByApp: (r: ByDimensionRow) => number | null;
  /** Итог за весь ряд. Нужен там, где сумма точек не равна значению за
   *  период: у отношений (TTR, CPT, CR, CPA) итог выводится из сумм
   *  числителя и знаменателя, а не складывается из дневных. Пусто —
   *  прежнее поведение, сумма. */
  totalOf?: (series: DailyPoint[]) => number | null;
  fmt: (v: number | null) => string;
};

const fmtMoney = (v: number | null) =>
  v == null || Number.isNaN(v)
    ? "—"
    : v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtMoneyCompact = (v: number | null) =>
  v == null || Number.isNaN(v)
    ? "—"
    : v.toLocaleString("en-US", { maximumFractionDigits: 0 });
const fmtInt = (v: number | null) =>
  v == null || Number.isNaN(v) ? "—" : Math.round(v).toLocaleString("en-US");
const fmtX = (v: number | null) => (v == null || Number.isNaN(v) ? "—" : v.toFixed(2));
const fmtPct = (v: number | null) => (v == null || Number.isNaN(v) ? "—" : v.toFixed(2) + "%");

const num = (v: string | number | null | undefined): number | null => {
  if (v == null) return null;
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isNaN(n) ? null : n;
};

const safeDiv = (a: number | null, b: number | null): number | null => {
  if (a == null || b == null || b === 0) return null;
  return a / b;
};

export const METRICS: MetricDef[] = [
  {
    id: "spend", label: "Spend", short: "Spend", unit: "$", color: "var(--c-accent-600)",
    // Spend going DOWN is treated as bad (red). User context: we're
    // running growth campaigns — pulling back on spend means missed
    // opportunity, not a win. Cost-efficiency lives in CPA / trial
    // cost / CPT (goodDir: "down"), not in absolute spend.
    goodDir: "up", gated: false, isRate: false,
    fromKpis: (k) => num(k.spend_usd),
    fromSeries: (p) => num(p.spend_usd),
    fromRow: (r) => num(r.spend_usd),
    fromByApp: (r) => num(r.spend_usd),
    fmt: fmtMoney,
  },
  {
    id: "revenue", label: "Revenue (running)", short: "Revenue", unit: "$", color: "#1f9d55",
    goodDir: "up", gated: true, isRate: false,
    fromKpis: (k) => num(k.revenue_usd_running),
    fromSeries: (p) => num(p.revenue_usd),
    fromRow: (r) => num(r.revenue_usd),
    fromByApp: (r) => num(r.revenue_usd),
    fmt: fmtMoney,
  },
  {
    // Predicted D30 proceeds — experimental, hardcoded rules per
    // (org, country, product). Backend returns null when no rule
    // applies for the current org, so the KPI card will render an
    // em-dash for most users; only orgs with a registered rule
    // (APPLAYERS as of 2026-04-24) see a number.
    id: "predicted_revenue_d30",
    label: "Predicted Proceeds D30",
    short: "Pred. D30",
    unit: "$",
    color: "#0ea5e9",
    goodDir: "up",
    gated: true,
    isRate: false,
    fromKpis: (k) => num(k.predicted_revenue_d30),
    // Only produced at the overview-summary level — no per-day /
    // per-campaign / per-app breakdown for now.
    fromSeries: () => null,
    fromRow: () => null,
    fromByApp: () => null,
    fmt: fmtMoney,
  },
  {
    // Predicted ROAS at D30 — derived on the frontend as
    // predicted_revenue_d30 / spend_usd. Keeps the rule count small
    // (backend only needs to serve the proceeds number) and mirrors
    // how `roas` is already computed client-side for series/byApp.
    id: "predicted_roas_d30",
    label: "Predicted ROAS D30",
    short: "Pred. ROAS",
    unit: "×",
    color: "#0ea5e9",
    goodDir: "up",
    gated: true,
    isRate: true,
    fromKpis: (k) => safeDiv(num(k.predicted_revenue_d30), num(k.spend_usd)),
    fromSeries: () => null,
    fromRow: () => null,
    fromByApp: () => null,
    fmt: fmtX,
  },
  {
    // Predicted profit at D30 — predicted_revenue_d30 − spend for
    // the current window. Shows as em-dash for orgs without a rule
    // (predicted_revenue_d30 = null). Can go negative when spend
    // outpaces expected proceeds — that's the point, user wants to
    // see red early.
    id: "predicted_profit_d30",
    label: "Predicted Profit D30",
    short: "Pred. Profit",
    unit: "$",
    color: "#0ea5e9",
    goodDir: "up",
    gated: true,
    isRate: false,
    fromKpis: (k) => {
      const rev = num(k.predicted_revenue_d30);
      const spend = num(k.spend_usd);
      if (rev == null || spend == null) return null;
      return rev - spend;
    },
    fromSeries: () => null,
    fromRow: () => null,
    fromByApp: () => null,
    fmt: fmtMoney,
  },
  {
    id: "roas", label: "ROAS (running)", short: "ROAS", unit: "×", color: "#8b5cf6",
    goodDir: "up", gated: true, isRate: true,
    fromKpis: (k) => num(k.roas_running),
    fromSeries: (p) => safeDiv(num(p.revenue_usd), num(p.spend_usd)),
    fromRow: (r) => num(r.roas),
    fromByApp: (r) => safeDiv(num(r.revenue_usd), num(r.spend_usd)),
    fmt: fmtX,
  },
  {
    id: "roas_d4", label: "ROAS D4", short: "ROAS D4", unit: "×", color: "#7c3aed",
    goodDir: "up", gated: true, isRate: true,
    fromKpis: (k) => num(k.roas_d4),
    fromSeries: () => null,
    fromRow: (r) => num(r.roas_d4),
    fromByApp: () => null,
    fmt: fmtX,
  },
  {
    id: "roas_d7", label: "ROAS D7", short: "ROAS D7", unit: "×", color: "#6d28d9",
    goodDir: "up", gated: true, isRate: true,
    fromKpis: (k) => num(k.roas_d7),
    fromSeries: () => null,
    fromRow: (r) => num(r.roas_d7),
    fromByApp: () => null,
    fmt: fmtX,
  },
  {
    id: "roas_d30", label: "ROAS D30", short: "ROAS D30", unit: "×", color: "#5b21b6",
    goodDir: "up", gated: true, isRate: true,
    fromKpis: (k) => num(k.roas_d30),
    fromSeries: () => null,
    fromRow: (r) => num(r.roas_d30),
    fromByApp: () => null,
    fmt: fmtX,
  },
  {
    id: "installs", label: "Installs", short: "Installs", unit: "", color: "#e0833a",
    goodDir: "up", gated: false, isRate: false,
    fromKpis: (k) => k.installs,
    fromSeries: (p) => p.installs ?? null,
    fromRow: (r) => r.installs ?? null,
    fromByApp: (r) => r.installs ?? null,
    fmt: fmtInt,
  },
  {
    id: "trials", label: "Trials", short: "Trials", unit: "", color: "#2563eb",
    goodDir: "up", gated: true, isRate: false,
    fromKpis: (k) => k.trials,
    fromSeries: (p) => p.trials ?? null,
    fromRow: (r) => r.trials ?? null,
    fromByApp: (r) => r.trials ?? null,
    fmt: fmtInt,
  },
  {
    id: "subscriptions", label: "Subscriptions", short: "Subs", unit: "", color: "#0ea5e9",
    goodDir: "up", gated: true, isRate: false,
    // OverviewKpis.subscriptions is always filled (when the mapping
    // gate is open) — surface it on the KPI card. Earlier version
    // hardcoded null here, which surfaced as the "Subs" column
    // showing an em-dash even when the back-end had the right
    // number (see CTMOBI Screen Mirroring 2026-04-24).
    //
    // DailyPoint (chart series) doesn't carry subscriptions today,
    // so fromSeries stays null — adding it on the chart axis is a
    // separate change that needs a matching schema field.
    fromKpis: (k) => k.subscriptions,
    fromSeries: () => null,
    fromRow: (r) => r.subscriptions ?? null,
    fromByApp: (r) => r.subscriptions ?? null,
    fmt: fmtInt,
  },
  {
    id: "impressions", label: "Impressions", short: "Impr.", unit: "", color: "#64748b",
    goodDir: "up", gated: false, isRate: false,
    fromKpis: (k) => k.impressions,
    fromSeries: (p) => p.impressions ?? null,
    fromRow: (r) => r.impressions ?? null,
    fromByApp: (r) => r.impressions ?? null,
    fmt: fmtInt,
  },
  {
    id: "taps", label: "Taps", short: "Taps", unit: "", color: "#7c3aed",
    goodDir: "up", gated: false, isRate: false,
    fromKpis: (k) => k.taps,
    fromSeries: (p) => p.taps ?? null,
    fromRow: (r) => r.taps ?? null,
    fromByApp: (r) => r.taps ?? null,
    fmt: fmtInt,
  },
  {
    id: "ttr", label: "TTR (Taps / Impressions)", short: "TTR", unit: "%", color: "#059669",
    goodDir: "up", gated: false, isRate: true,
    fromKpis: (k) => {
      const v = safeDiv(k.taps, k.impressions);
      return v == null ? null : v * 100;
    },
    fromSeries: (p) => {
      const v = safeDiv(p.taps ?? null, p.impressions ?? null);
      return v == null ? null : v * 100;
    },
    fromRow: (r) => {
      const v = safeDiv(r.taps ?? null, r.impressions ?? null);
      return v == null ? null : v * 100;
    },
    fromByApp: (r) => {
      const v = safeDiv(r.taps ?? null, r.impressions ?? null);
      return v == null ? null : v * 100;
    },
    fmt: fmtPct,
  },
  {
    id: "cpt", label: "Avg CPT", short: "CPT", unit: "$", color: "#a16207",
    goodDir: "down", gated: false, isRate: true,
    fromKpis: (k) => safeDiv(num(k.spend_usd), k.taps),
    fromSeries: (p) => safeDiv(num(p.spend_usd), p.taps ?? null),
    fromRow: (r) => safeDiv(num(r.spend_usd), r.taps ?? null),
    fromByApp: (r) => safeDiv(num(r.spend_usd), r.taps ?? null),
    fmt: fmtMoney,
  },
  {
    id: "cpa", label: "CPA (Install)", short: "CPA", unit: "$", color: "#ef4444",
    goodDir: "down", gated: false, isRate: true,
    fromKpis: (k) => safeDiv(num(k.spend_usd), k.installs),
    fromSeries: (p) => safeDiv(num(p.spend_usd), p.installs ?? null),
    fromRow: (r) => num(r.cpa),
    fromByApp: (r) => safeDiv(num(r.spend_usd), r.installs ?? null),
    fmt: fmtMoney,
  },
  {
    id: "trialCvr", label: "Trial CVR", short: "Trial CVR", unit: "%", color: "#0ea5e9",
    goodDir: "up", gated: true, isRate: true,
    fromKpis: (k) => {
      const v = safeDiv(k.trials, k.installs);
      return v == null ? null : v * 100;
    },
    fromSeries: (p) => {
      const v = safeDiv(p.trials ?? null, p.installs ?? null);
      return v == null ? null : v * 100;
    },
    fromRow: (r) => {
      const v = num(r.trial_cvr);
      return v == null ? null : v * 100;
    },
    fromByApp: (r) => {
      const v = safeDiv(r.trials ?? null, r.installs ?? null);
      return v == null ? null : v * 100;
    },
    fmt: fmtPct,
  },
  {
    id: "trialCpa", label: "Cost / Trial", short: "Trial cost", unit: "$", color: "#b45309",
    goodDir: "down", gated: true, isRate: true,
    fromKpis: (k) => safeDiv(num(k.spend_usd), k.trials),
    fromSeries: (p) => safeDiv(num(p.spend_usd), p.trials ?? null),
    fromRow: (r) => safeDiv(num(r.spend_usd), r.trials ?? null),
    fromByApp: (r) => safeDiv(num(r.spend_usd), r.trials ?? null),
    fmt: fmtMoney,
  },
];

export const METRIC_MAP: Record<string, MetricDef> = Object.fromEntries(
  METRICS.map((m) => [m.id, m])
);

export function getMetric(id: string): MetricDef {
  return METRIC_MAP[id] || METRICS[0]!;
}

/** Метрика реестра по идентификатору — или ничего. В отличие от
 *  `getMetric`, НЕ подставляет Spend: там, где идентификатор мог прийти
 *  извне (сохранённый выбор, метрика по событию), «нет такой» — ответ,
 *  а Spend вместо неё — подмена, которую заметят только по легенде. */
export function findMetric(id: string): MetricDef | undefined {
  return METRIC_MAP[id];
}

export function fmtMetricValue(id: string, v: number | null): string {
  const m = getMetric(id);
  const prefix = m.unit === "$" ? "$" : "";
  const suffix = m.unit && m.unit !== "$" ? m.unit : "";
  return prefix + m.fmt(v) + suffix;
}

/**
 * For rate metrics (ROAS/CPA/CVR/CPT/TTR), aggregating a series of
 * daily ratios by sum or average is wrong — recompute from the
 * underlying volume sums instead.
 */
export function aggregateSeries(
  id: string,
  series: DailyPoint[]
): number | null {
  const m = getMetric(id);
  if (series.length === 0) return null;
  if (!m.isRate) {
    let total = 0;
    let seen = 0;
    for (const p of series) {
      const v = m.fromSeries(p);
      if (v != null) {
        total += v;
        seen++;
      }
    }
    return seen === 0 ? null : total;
  }
  // Recompute rate from series totals.
  const spend = series.reduce((s, p) => s + (num(p.spend_usd) ?? 0), 0);
  const installs = series.reduce((s, p) => s + (p.installs ?? 0), 0);
  const trials = series.reduce((s, p) => s + (p.trials ?? 0), 0);
  const impressions = series.reduce((s, p) => s + (p.impressions ?? 0), 0);
  const taps = series.reduce((s, p) => s + (p.taps ?? 0), 0);
  const revenue = series.reduce((s, p) => s + (num(p.revenue_usd) ?? 0), 0);
  switch (id) {
    case "roas":
      return safeDiv(revenue, spend);
    case "cpa":
      return safeDiv(spend, installs);
    case "cpt":
      return safeDiv(spend, taps);
    case "trialCpa":
      return safeDiv(spend, trials);
    case "trialCvr": {
      const v = safeDiv(trials, installs);
      return v == null ? null : v * 100;
    }
    case "ttr": {
      const v = safeDiv(taps, impressions);
      return v == null ? null : v * 100;
    }
    default:
      return null;
  }
}

export { fmtMoney, fmtMoneyCompact, fmtInt, fmtX, fmtPct };
